// @flow

import * as React from 'react';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { AddAllButton } from 'src/Domain/AddAllButton';
import { useAddAllProvider, MIN_ELEMENTS_TO_BULK } from 'src/Domain/modules';
import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectExtendedResults } from '../modules/selectors';

import type { ExtendedResult } from '../types';

import { DomainRow } from './DomainRow';
import { UnavailableRow } from './UnavailableRow';
import {
  addAllToCart,
  renewPossibility2AvailabilityIconName,
  useSortResultsByPossibility,
} from './modules';

import type { DomainRenewPossibility, GroupedResults } from './modules';

import styles from './DomainList.scss';

function ResultsGroup({
  results,
}: {
  results: GroupedResults['canAddToCartResults' | 'otherResults'],
}): Array<null | React.MixedElement> {
  return Object.keys(results).map((possibility: DomainRenewPossibility) => {
    // $FlowFixMe[prop-missing]
    const resultGroup = results[possibility] as Array<ExtendedResult>;

    if (!resultGroup || resultGroup.length === 0) return null;
    const availabilityIconName =
      renewPossibility2AvailabilityIconName[possibility];

    return (
      <div key={possibility} className={styles.list}>
        {resultGroup.map((result: ExtendedResult) => (
          <ErrorBoundary
            key={result.fqdn}
            fallback={<UnavailableRow.Error fqdn={result.fqdn} />}
          >
            {availabilityIconName === 'ok' ? (
              <DomainRow key={result.fqdn} domain={result} />
            ) : (
              <UnavailableRow key={result.fqdn} result={result} />
            )}
          </ErrorBoundary>
        ))}
      </div>
    );
  });
}

export function DomainList(): React.MixedElement | null {
  const translator = useTranslator();
  const allResults = useAppSelector(selectExtendedResults);
  const authenticated = useAppSelector(selectIsAuthenticatedUser);

  const { canAddToCartResults, otherResults } = useSortResultsByPossibility();

  const hasResults = allResults.length > 0;

  const { loading, handleClick } = useAddAllProvider({
    addAllAction: addAllToCart(translator),
  });

  const allCanAddToCartResults = [
    ...canAddToCartResults.renewable,
    ...canAddToCartResults.restorable,
  ];
  const showBulk =
    allCanAddToCartResults.length >= MIN_ELEMENTS_TO_BULK &&
    allCanAddToCartResults.find(result => !result.addedToCart);

  // we should show a suggested renew block instead
  if (authenticated && !hasResults) return null;
  return (
    <Base
      flex
      flexCol
      marginBottom={hasResults ? 'base' : 'large'}
      className={styles.root}
    >
      <div className={styles.container}>
        {showBulk && <AddAllButton loading={loading} onClick={handleClick} />}
        <ResultsGroup results={canAddToCartResults} />
        {showBulk && <AddAllButton loading={loading} onClick={handleClick} />}
      </div>

      <ResultsGroup results={otherResults} />
    </Base>
  );
}
